import { getI18nStrings } from "../config/runtime";
import { csrfToken } from "../core/http";
import { activeClientLocale, localizedRawPath } from "../core/localePaths";

function readAuth() {
  try {
    const el = document.getElementById("ecliptica-auth-bootstrap");
    if (!el?.textContent) return { signedIn: false };
    return JSON.parse(el.textContent);
  } catch {
    return { signedIn: false };
  }
}

function signInUrl() {
  const path = `${window.location.pathname}${window.location.search}`;
  const loginPath = localizedRawPath("/sign-in", activeClientLocale());
  return `${loginPath}?return_to=${encodeURIComponent(path)}`;
}

async function requestJson(url, method, body) {
  const response = await fetch(url, {
    method,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    credentials: "same-origin",
    body: body ? JSON.stringify(body) : undefined,
  });
  const nextToken = response.headers.get("X-CSRF-Token");
  if (nextToken) {
    const meta = document.querySelector("meta[name='csrf-token']");
    if (meta) meta.setAttribute("content", nextToken);
  }
  const json = await response.json().catch(() => ({}));
  if (response.status === 401) {
    window.location.assign(signInUrl());
    return null;
  }
  if (!response.ok) {
    const table = getI18nStrings();
    const loc = activeClientLocale();
    const fallback = table[loc]?.http_request_failed || table.en?.http_request_failed || "Request failed";
    throw new Error(json.error || fallback);
  }
  return json;
}

function updateUi(root, payload) {
  const likesEl = root.querySelector('[data-count="likes"]');
  const dislikesEl = root.querySelector('[data-count="dislikes"]');
  if (likesEl) likesEl.textContent = String(payload.likes_count ?? 0);
  if (dislikesEl) dislikesEl.textContent = String(payload.dislikes_count ?? 0);

  const likeBtn = root.querySelector('[data-reaction="like"]');
  const dislikeBtn = root.querySelector('[data-reaction="dislike"]');
  const reaction = payload.reaction;
  if (likeBtn) {
    likeBtn.classList.toggle("is-active", reaction === "like");
    likeBtn.setAttribute("aria-pressed", reaction === "like" ? "true" : "false");
  }
  if (dislikeBtn) {
    dislikeBtn.classList.toggle("is-active", reaction === "dislike");
    dislikeBtn.setAttribute("aria-pressed", reaction === "dislike" ? "true" : "false");
  }
}

export function initArticleReactions() {
  const root = document.getElementById("article-reactions");
  if (!root) return;

  const slug = root.dataset.articleSlug;
  if (!slug) return;

  const apiBase = `/api/v1/articles/${encodeURIComponent(slug)}/reaction`;

  const applyInitial = () => {
    updateUi(root, {
      reaction: root.dataset.initialReaction || null,
      likes_count: Number(root.dataset.likesCount) || 0,
      dislikes_count: Number(root.dataset.dislikesCount) || 0,
    });
  };

  applyInitial();

  const onToggle = async (value) => {
    const auth = readAuth();
    if (!auth.signedIn) {
      window.location.assign(signInUrl());
      return;
    }

    const current = root.dataset.initialReaction || null;
    const isActive = current === value;

    try {
      let payload;
      if (isActive) {
        payload = await requestJson(apiBase, "DELETE");
      } else {
        payload = await requestJson(apiBase, "POST", { value });
      }
      if (!payload) return;
      root.dataset.initialReaction = payload.reaction || "";
      updateUi(root, payload);
    } catch (err) {
      const hint = root.querySelector("[data-reaction-hint]");
      if (hint) {
        hint.hidden = false;
        hint.textContent = err.message || "Could not save your reaction.";
      }
    }
  };

  root.querySelector('[data-reaction="like"]')?.addEventListener("click", () => onToggle("like"));
  root.querySelector('[data-reaction="dislike"]')?.addEventListener("click", () => onToggle("dislike"));
}
